import { Component } from '@theme/component';
import { CartAddEvent, ThemeEvents } from '@theme/events';

const ANNOUNCEMENT_DURATION = 3000;

/**
 * Inline size picker shown on product cards.
 *
 * Each size button carries its variant id via the declarative binding
 * `on:click="plf-size-picker/selectSize/<variantId>"`. Selecting a size writes that
 * id into the form's hidden `id` input and submits, which hands off to
 * ProductFormComponent's existing add-to-cart flow — no cart logic is duplicated here.
 *
 * ProductFormComponent only announces the result when it can find an
 * `add-to-cart-component` to read the wording from. The size picker has no such
 * button, so it listens for the cart events itself and announces into its own
 * live region — otherwise adding a size would be completely silent.
 *
 * @extends {Component<{ statusRegion?: HTMLElement }>}
 */
class PlfSizePicker extends Component {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #clearTimer;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(ThemeEvents.cartError, this.#handleCartError);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(ThemeEvents.cartError, this.#handleCartError);
    clearTimeout(this.#clearTimer);
  }

  /**
   * Adds the chosen size to the cart.
   *
   * @param {string | number} variantId - Variant id of the chosen size.
   * @param {Event} [event]
   */
  selectSize(variantId, event) {
    event?.preventDefault();

    const form = this.querySelector('form');
    const input = this.querySelector('input[name="id"]');

    if (!(form instanceof HTMLFormElement) || !(input instanceof HTMLInputElement)) return;

    input.value = String(variantId);
    input.removeAttribute('disabled');

    // requestSubmit fires a cancelable submit event, which is what
    // product-form-component listens for. Fall back for older engines.
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
  }

  #handleCartAdd = () => {
    this.#announce(globalThis.Theme?.translations?.added ?? '');
  };

  /** @param {Event} event */
  #handleCartError = (event) => {
    const detail = /** @type {{ detail?: { data?: { message?: string } } }} */ (event).detail;
    this.#announce(detail?.data?.message ?? '');
  };

  /** @param {string} message */
  #announce(message) {
    const region = this.refs.statusRegion;

    if (!region || !message) return;

    clearTimeout(this.#clearTimer);
    region.textContent = message;

    this.#clearTimer = setTimeout(() => {
      region.textContent = '';
    }, ANNOUNCEMENT_DURATION);
  }
}

if (!customElements.get('plf-size-picker')) {
  customElements.define('plf-size-picker', PlfSizePicker);
}
